import { useState, useEffect, useCallback } from "react";

type CameraPermissionState = "checking" | "prompt" | "granted" | "denied" | "unsupported";

interface UseCameraReturn {
  permissionState: CameraPermissionState;
  requestPermission: () => Promise<void>;
}

export const useCamera = (): UseCameraReturn => {
  const [permissionState, setPermissionState] = useState<CameraPermissionState>("checking");

  useEffect(() => {
    if (!navigator.mediaDevices) {
      setPermissionState("unsupported");
      return;
    }

    if (!navigator.permissions) {
      setPermissionState("prompt");
      return;
    }

    let permStatus: PermissionStatus | null = null;

    const handleChange = () => {
      if (!permStatus) return;
      const state = permStatus.state;
      if (state === "granted") {
        setPermissionState("granted");
      } else if (state === "denied") {
        setPermissionState("denied");
      } else {
        setPermissionState("prompt");
      }
    };

    navigator.permissions
      .query({ name: "camera" as PermissionName })
      .then((status) => {
        permStatus = status;
        if (status.state === "granted") {
          setPermissionState("granted");
        } else if (status.state === "denied") {
          setPermissionState("denied");
        } else {
          setPermissionState("prompt");
        }
        status.addEventListener("change", handleChange);
      })
      .catch(() => {
        setPermissionState("prompt");
      });

    return () => {
      permStatus?.removeEventListener("change", handleChange);
    };
  }, []);

  const requestPermission = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionState("unsupported");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      setPermissionState("granted");
    } catch (err) {
      if (err instanceof DOMException) {
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          setPermissionState("denied");
        } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
          setPermissionState("unsupported");
        }
      }
    }
  }, []);

  return { permissionState, requestPermission };
};
